import { Box, IconButton, Tooltip, Typography } from "@mui/material";
import { BasicTableRowStyles, TableHeaderWrapper } from "../../../../components/common/Common.styles";
import ServerSideTableColumnHeader from "../../../../components/common/table/ServerSideTableColumnHeader";
import BorderRow from "../../../../components/common/table/BorderRow";
import { useTranslation } from "react-i18next";
import { useEffect, useState } from "react";
import CellBox from "../../../../components/common/table/CellBox";
import IncentivationInputField from "./IncentivationInputField";
import { useEditMode } from "../../../../store/selectors/tableManagementSelector";
import SubtypeSummaryCell from "./subtype/SubtypeSummaryCell";
import InstanceSubtypeDetailsCell from "./subtype/InstanceSubtypeDetailsDialog";
import dayjs, { Dayjs } from "dayjs";
import InstanceStatusIcon from "./InstanceStatusIcon";
import { actionModulesApi } from "../../../../api/actionsModulesApi";
import { ActionModule, ActionModuleEmployeeResponse } from "../../../../types/actionModule/actionModule";
import { ActionModuleInstanceResponse } from "../../../../types/actionModule/actionModuleInstance";
import { BaseRewardCalculationInformation } from "../../../../types/actionModule/rewardCalculationInformation";
import { useAppDispatch } from "../../../../hooks/StoreHooks";
import { bookTransactionForActionModuleInstanceThunk } from "../../../../store/thunk/actionsModulesThunk";
import { TransactionIcon } from "../../../../assets/icons/Icons";
import ConfirmDialog from "../../../../components/common/ConfirmDialog";
import { LeftStickyBox, RightStickyBox } from "./EmployeeInstancesIncentivationTable.styles";

interface Props {
    actionModule: ActionModule;
    employeeRow: ActionModuleEmployeeResponse;
    startDate: Dayjs;
    endDate: Dayjs;
    onIncentivationUpdated: (
        instanceId: number,
        rewardCalculationInformation: BaseRewardCalculationInformation
    ) => void;
}

export default function EmployeeInstancesIncentivationTable(props: Props) {
    const subtypeColumnWidth = 255;
    const fixedColumnsWidth = 760;
    const subtypeColumnsWidth = props.actionModule.subtypes.length * subtypeColumnWidth;
    const subtypeTableTemplate = `${subtypeColumnWidth}px `.repeat(props.actionModule.subtypes.length);
    const fixedTableTemplate = `100px 200px 150px 150px 100px 60px`; // sum = 760px
    const [instances, setInstances] = useState<ActionModuleInstanceResponse[]>([]);

    useEffect(() => {
        const fetchInstances = async () => {
            var res = await actionModulesApi.getActionModuleInstances(
                props.actionModule.id,
                props.employeeRow.id,
                props.startDate,
                props.endDate
            )
            setInstances(res.result ?? []);
        };
        fetchInstances();
    }, [props.actionModule]);

    const handleOnInstanceChanged = (oldInstance, changedInstance) => {
        setInstances(oldState => oldState.map(i => i !== oldInstance ? i : changedInstance))
    };

    return <Box sx={{ overflowX: "auto", width: "100%" }}>
        <Box sx={{ maxWidth: subtypeColumnsWidth + fixedColumnsWidth, display: "block" }}>
            <TableHeader
                leftTableTemplate={subtypeTableTemplate}
                rightTableTemplate={fixedTableTemplate}
                actionModule={props.actionModule} />
            <Box sx={{ display: "contents" }}>
                { instances.map(i => <TableRow
                    key={i.id}
                    leftTableTemplate={subtypeTableTemplate}
                    rightTableTemplate={fixedTableTemplate}
                    actionModule={props.actionModule}
                    employeeRow={props.employeeRow}
                    instance={i}
                    onIncentivationUpdated={props.onIncentivationUpdated}
                    onInstanceChanged={(updated) => handleOnInstanceChanged(i, updated)}
                />)}
            </Box>
        </Box>
        <BorderRow />
    </Box>
}

interface TableHeaderProps {
    actionModule: ActionModule;
    leftTableTemplate: string;
    rightTableTemplate: string;
}

function TableHeader(props: TableHeaderProps) {
    const { t } = useTranslation();

    return <Box sx={{ display: "flex" }}>
        <TableHeaderWrapper>
            <LeftStickyBox>
                <ServerSideTableColumnHeader
                    isSorted={false}
                    label={""}
                />
            </LeftStickyBox>
            
            <Box gridTemplateColumns={props.leftTableTemplate} display="grid">
                { props.actionModule.subtypes
                    .map((s, i) => <ServerSideTableColumnHeader
                        firstCol={i === 0}
                        label={s.name ?? ""}
                    />)
                }
            </Box>
            
            <RightStickyBox>
                <Box gridTemplateColumns={props.rightTableTemplate} display="grid">
                    <ServerSideTableColumnHeader
                        isSorted={false}
                        label={t("incentivationPage.instanceLifetime")}
                    />
                    <ServerSideTableColumnHeader
                        isSorted={false}
                        label={t("tableColumnHeaders.dateOfAction")}
                    />
                    <ServerSideTableColumnHeader
                        isSorted={false}
                        label={t("incentivationPage.incentivation")}
                    />
                    <ServerSideTableColumnHeader
                        fieldName="points"
                        label={t("tableColumnHeaders.points")}
                    />
                    <ServerSideTableColumnHeader
                        isSorted={false}
                        label={t("tableColumnHeaders.status")}
                    />
                    <ServerSideTableColumnHeader
                        isSorted={false}
                        label=""
                    />
                </Box>
            </RightStickyBox>
        </TableHeaderWrapper>
    </Box>
}

interface TableRowProps {
    leftTableTemplate: string;
    rightTableTemplate: string;
    actionModule: ActionModule;
    employeeRow: ActionModuleEmployeeResponse;
    instance: ActionModuleInstanceResponse;
    onIncentivationUpdated: (
        instanceId: number,
        rewardCalculationInformation: BaseRewardCalculationInformation
    ) => void;
    onInstanceChanged: (updatedInstance: ActionModuleInstanceResponse) => void;
}

function TableRow(props: TableRowProps) {
    const editMode = useEditMode();
    const dispatch = useAppDispatch();
    const { t } = useTranslation()

    const [confirmTransactionOpen, setConfirmTransactionOpen] = useState(false);
    const handleConfirmTransactionOpen = () => setConfirmTransactionOpen(true);
    const handleConfirmTransactionClose = () => setConfirmTransactionOpen(false);
    const handleConfirmTransaction = async () => {
        await dispatch(
            bookTransactionForActionModuleInstanceThunk({
                actionModuleId: props.actionModule.id.toString(),
                actionModuleInstanceId: props.instance.id.toString(),
            })
        );
        setConfirmTransactionOpen(false);
    };

    return (
        <Box sx={{display: "flex" }}>
            <BasicTableRowStyles>
                <LeftStickyBox>
                    <CellBox >
                        <InstanceSubtypeDetailsCell
                            actionModule={props.actionModule}
                            instance={props.instance} />
                    </CellBox>
                </LeftStickyBox>
                
                <Box gridTemplateColumns={props.leftTableTemplate} display="grid">
                    { props.actionModule.subtypes.map((s, i) => <CellBox 
                        firstCol={i === 0} 
                        sx={{ justifyContent: "flex-start" }}
                    >
                        <SubtypeSummaryCell
                            actionModule={props.actionModule}
                            instance={props.instance}
                            subtype={s}
                        />
                    </CellBox>)}
                </Box>

                <RightStickyBox>
                    <Box gridTemplateColumns={props.rightTableTemplate} display="grid">
                        <CellBox>
                            <Typography variant="caption">
                                {dayjs(props.instance.startDate).format("DD.MM.YYYY")} - {dayjs(props.instance.endDate).format("DD.MM.YYYY")}
                            </Typography>
                        </CellBox>
                        <CellBox>
                            <Typography variant="caption">
                                {props.instance.completedAt
                                    ? dayjs(props.instance.completedAt).format("DD.MM.YYYY")
                                    : ""}
                            </Typography>
                        </CellBox>
                        <CellBox>
                            <IncentivationInputField
                                actionModule={props.actionModule}
                                isEditMode={editMode}
                                employeeStatus={props.employeeRow.status}
                                incentivationData={props.instance.incentivationData}
                                onChange={(updated) => {
                                    props.onIncentivationUpdated(
                                        props.instance.id,
                                        updated
                                    );
                                    props.onInstanceChanged({
                                        ...props.instance,
                                        incentivationData: {
                                            ...props.instance.incentivationData!,
                                            ...updated
                                        }
                                    });
                                }} />
                        </CellBox>
                        <CellBox>
                            <Typography>{props.instance.incentivationData?.reward ?? "-"}</Typography>
                        </CellBox>
                        <CellBox>
                            <InstanceStatusIcon status={props.instance.status} />
                        </CellBox>
                        <CellBox sx={{
                                display: "flex",
                                justifyContent: "center",
                                alignItems: "center",
                        }}>
                            <Tooltip title={t("buttons.bookTransaction")}>
                                <IconButton
                                    size="medium"
                                    onClick={handleConfirmTransactionOpen}
                                >
                                    <TransactionIcon colorHex="#757575" />
                                </IconButton>
                            </Tooltip>
                        </CellBox>
                    </Box>
                </RightStickyBox>
            </BasicTableRowStyles>

            <ConfirmDialog
                open={confirmTransactionOpen}
                title={t("buttons.bookTransaction")}
                message={t("dialogModals.confirmTransactionActionModuleInstance")}
                onClose={handleConfirmTransactionClose}
                onConfirm={handleConfirmTransaction}
            />
        </Box>
    );
}